import React from 'react';
import { Empty, Spin, Typography } from 'antd';

import { useQuery } from '@tanstack/react-query';

import { useTradeBookStore } from '../../../../store';
import { TRADEBOOK } from '../../../../utilities/functions';

import SetOverviewPanel from './SetOverviewPanel';
import SetTypeSidePanel from './SetTypeSidePanel';

import './styles.scss';

const { Text, Title } = Typography;

export default function StrategySetsTab() {
    const { selectedPlan, selectedSetType, setSelectedSetType } = useTradeBookStore();

    // Fetch strategy sets data for the selected trading plan
    const {
        isLoading,
        data: setsData,
        error,
        refetch
    } = useQuery(
        ['strategy-sets', selectedPlan],
        () => TRADEBOOK.fetchStrategySetsForPlan(selectedPlan),
        {
            enabled: !!selectedPlan,
            retry: false,
            staleTime: 30000, // 30 seconds
            refetchOnWindowFocus: true
        }
    );

    // Show empty state when no trading plan is selected
    if (!selectedPlan) {
        return (
            <div className="strategy-sets-empty">
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div>
                            <Title level={5}>No Trading Plan Selected</Title>
                            <Text type="secondary">
                                Select a trading plan from the left sidebar to view its Strategy
                                Sets.
                            </Text>
                        </div>
                    }
                />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="strategy-sets-loading">
                <Spin size="large" />
                <Text>Loading Strategy Sets...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div className="strategy-sets-error">
                <Text type="danger">Failed to load strategy sets. Please try again.</Text>
            </div>
        );
    }

    // Map set type to data key
    const setTypeDataMap = {
        strategy: 'strategySets',
        noStrategy: 'noStrategySets'
    };

    const currentSets = setsData?.[setTypeDataMap[selectedSetType]] || [];

    // Calculate totals for each set type (for badges)
    const setCounts = {
        strategy: setsData?.strategySets?.length || 0,
        noStrategy: setsData?.noStrategySets?.length || 0
    };

    return (
        <div className="strategy-sets-tab">
            <div className="strategy-sets-header">
                <Title level={4}>{setsData?.strategyName} - Strategy Sets</Title>
            </div>
            <div className="strategy-sets-content">
                <SetTypeSidePanel
                    selectedSetType={selectedSetType}
                    onSelectSetType={setSelectedSetType}
                    setCounts={setCounts}
                />
                <SetOverviewPanel
                    setType={selectedSetType}
                    sets={currentSets}
                    planId={selectedPlan}
                    onRefresh={refetch}
                />
            </div>
        </div>
    );
}
